/**
 * GamesPage — league-scoped game browser at /leagues/:leagueId/games.
 *
 * Sub-tabs: Scheduled | Results | Proposals
 * Category filter row + status filter.
 * Per-row contextual actions:
 *   - "Enter result" on a scheduled game involving the viewer's team
 *   - "Approve" on a game whose result awaits the viewer's team
 * Browse is public; mutating actions require auth.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { fetchGames, fetchProposals, acceptProposal } from '../../store/slices/gameSlice';
import { fetchMyTeams } from '../../store/slices/teamSlice';
import { fetchLeague, fetchSeasons } from '../../store/slices/leagueSlice';
import type { Game, GameProposal } from '@sulis-liiga/common';
import { GameStatus, GameProposalStatus, Category, SeasonStatus } from '@sulis-liiga/common';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';
import { gameToRowInput, proposalToRowInput, type BannerContext } from '../../utils/gameBanner';
import ScopeToggle, { type Scope } from '../../components/common/ScopeToggle';
import GameSummaryRow, { toGameRowProps } from '../../components/game/GameSummaryRow';

// ─── Types ────────────────────────────────────────────────────────────────────

type Tab = 'proposals' | 'scheduled' | 'results' | 'pending';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonRow: React.FC = () => (
  <div className="px-4 py-4 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="h-4 bg-gray-200 rounded w-48" />
      <div className="h-5 bg-gray-200 rounded-full w-20" />
    </div>
    <div className="mt-2 h-3 bg-gray-200 rounded w-64" />
  </div>
);

// ─── GamesPage ────────────────────────────────────────────────────────────────

const GamesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { leagueId } = useParams<{ leagueId: string }>();
  const [searchParams] = useSearchParams();

  const { games, proposals, loading, error } = useSelector((state: RootState) => state.game);
  const { myTeams } = useSelector((state: RootState) => state.team);
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { seasons } = useSelector((state: RootState) => state.league);

  // Games are shown for the league's active season only.
  const activeSeason = useMemo(
    () => seasons.find(s => s.status === SeasonStatus.ACTIVE),
    [seasons],
  );

  // Allow deep-linking to a tab (e.g. Home's "More results" → ?tab=results).
  const tabParam = searchParams.get('tab');
  const initialTab: Tab =
    tabParam === 'results' || tabParam === 'proposals' || tabParam === 'pending'
      ? tabParam
      : 'scheduled';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  // Scope: signed-in players default to their own games/proposals and can expand
  // to everything in the division. Logged-out visitors always see all (there is
  // no "mine" for them).
  const [scope, setScope] = useState<Scope>('mine');
  const showOnlyMine = isAuthenticated && scope === 'mine';

  // Resolve tenant slug from URL leagueId — gates data fetches to avoid registry leaks.
  const { resolved: tenantResolved, leagueName: selectedLeagueName } =
    useActiveLeagueTenant(leagueId);

  // Fetch the league entity + all seasons (so the active season can be
  // resolved) — only after tenant slug is resolved.
  useEffect(() => {
    if (leagueId && tenantResolved) {
      dispatch(fetchLeague(leagueId));
      dispatch(fetchSeasons(leagueId));
    }
  }, [dispatch, leagueId, tenantResolved]);

  // Fetch games scoped to the active season only, once tenant slug + the
  // active season are resolved (so axios prefixes correctly and results
  // aren't clipped to the most-recent season). When there is no active
  // season, no games are fetched — the page shows an empty-state message.
  useEffect(() => {
    if (tenantResolved && activeSeason?.id) {
      dispatch(fetchGames({ seasonId: activeSeason.id }));
    }
  }, [dispatch, tenantResolved, activeSeason?.id]);

  // Fetch proposals and my teams only when authenticated and tenant resolved.
  // fetchMyTeams is league-scoped — must not hit the registry.
  useEffect(() => {
    if (isAuthenticated && tenantResolved) {
      dispatch(fetchProposals(undefined));
      dispatch(fetchMyTeams());
    }
  }, [dispatch, isAuthenticated, tenantResolved]);

  // My team IDs (for contextual actions)
  const myTeamIds = useMemo(() => new Set(myTeams.map(t => t.id)), [myTeams]);

  // Viewer context for the shared banner builder (single source of truth for the
  // status label + contextual action, shared with Home and the Divisions page).
  const bannerCtx: BannerContext = useMemo(
    () => ({ myTeamIds, userId: user?.id }),
    [myTeamIds, user?.id],
  );

  // One-click proposal approval: accept the (single) offered slot, which schedules
  // the game (land on it if the API returns its id). Only invoked by the banner for
  // single-slot proposals; multi-slot ones link to the proposal detail instead.
  const handleApproveProposal = async (proposalId: string) => {
    try {
      const accepted = await dispatch(acceptProposal({ proposalId, slotIndex: 0 })).unwrap();
      navigate(accepted.scheduledGameId ? `/games/${accepted.scheduledGameId}` : '/games');
    } catch {
      // Surface nothing inline here; the proposal detail page shows errors.
    }
  };

  // Filter and partition games by tab. When scoped to "mine", keep only games that
  // involve one of the viewer's teams. (Proposals are always personal — the API only
  // returns proposals involving the viewer's teams — so the scope toggle does not
  // apply to them and is hidden on the Proposals tab.)
  const scheduledGames = useMemo(
    () =>
      games.filter(
        g =>
          g.status === GameStatus.SCHEDULED &&
          (!categoryFilter || g.divisionCategory === categoryFilter) &&
          (!showOnlyMine || g.teamIds.some(id => myTeamIds.has(id))),
      ),
    [games, showOnlyMine, myTeamIds, categoryFilter],
  );
  const completedGames = useMemo(
    () =>
      games.filter(
        g =>
          (g.status === GameStatus.COMPLETED || g.status === GameStatus.DISPUTED) &&
          (!categoryFilter || g.divisionCategory === categoryFilter) &&
          (!showOnlyMine || g.teamIds.some(id => myTeamIds.has(id))),
      ),
    [games, showOnlyMine, myTeamIds, categoryFilter],
  );
  // Pending fixtures (pre-built round-robin games not yet scheduled/played). These
  // are "Pending to Propose" or, if an active proposal exists, "Pending Proposal".
  const pendingGames = useMemo(
    () =>
      games.filter(
        g =>
          g.status === GameStatus.PENDING &&
          (!categoryFilter || g.divisionCategory === categoryFilter) &&
          (!showOnlyMine || g.teamIds.some(id => myTeamIds.has(id))),
      ),
    [games, showOnlyMine, myTeamIds, categoryFilter],
  );
  // Proposals are always personal (server-scoped). Show only open proposals —
  // accepted (now a scheduled game), declined, expired and cancelled ones drop off.
  // The category filter still applies.
  const visibleProposals = useMemo(
    () =>
      proposals.filter(
        p =>
          (p.status === GameProposalStatus.PENDING || p.status === GameProposalStatus.COUNTERED) &&
          (!categoryFilter || p.divisionCategory === categoryFilter),
      ),
    [proposals, categoryFilter],
  );

  // Pending proposals count for tab badge (respects the category filter).
  const pendingProposalsCount = visibleProposals.filter(
    p => p.status === GameProposalStatus.PENDING,
  ).length;

  // Handle propose action (requires auth)
  const handlePropose = () => {
    if (!isAuthenticated) {
      navigate(`/login?returnTo=/games/proposals/create`);
      return;
    }
    navigate('/games/proposals/create');
  };

  // Record an already-played game (with its result) directly.
  const handleRecord = () => {
    if (!isAuthenticated) {
      navigate(`/login?returnTo=/games/record`);
      return;
    }
    navigate('/games/record');
  };

  // Group games by date for display
  const groupByDate = (gs: Game[]): Map<string, Game[]> => {
    const map = new Map<string, Game[]>();
    for (const g of gs) {
      const key = g.scheduledDate ? formatDate(g.scheduledDate) : 'Unscheduled';
      if (!map.has(key)) map.set(key, []);
      const bucket = map.get(key);
      if (bucket) bucket.push(g);
    }
    return map;
  };

  const scheduledGroups = useMemo(() => groupByDate(scheduledGames), [scheduledGames]);
  const completedGroups = useMemo(() => groupByDate(completedGames), [completedGames]);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Page header */}
      <div className="pb-4 border-b border-gray-200 flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Games</h1>
          {selectedLeagueName && <p className="text-sm text-gray-500 mt-1">{selectedLeagueName}</p>}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleRecord}
            className="px-4 py-2 rounded-md text-sm font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 transition-colors"
          >
            Record a game
          </button>
          <button
            type="button"
            onClick={handlePropose}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            Propose a game
          </button>
        </div>
      </div>

      {/* No active season — nothing to show; sub-tabs/filters are hidden. */}
      {!activeSeason && (
        <div className="mt-6 text-center py-12 bg-white rounded-lg shadow-sm">
          <p className="text-gray-500">There are no active seasons.</p>
        </div>
      )}

      {activeSeason && (
        <>
          {/* Sub-tabs */}
          <nav
            className="mt-4 flex gap-6 border-b border-gray-200 text-sm"
            aria-label="Game views"
            role="tablist"
          >
            {(
              [
                {
                  id: 'proposals',
                  label: 'Proposals',
                  badge: pendingProposalsCount > 0 ? pendingProposalsCount : undefined,
                },
                { id: 'scheduled', label: 'Scheduled' },
                { id: 'results', label: 'Results' },
                {
                  id: 'pending',
                  label: 'Pending',
                  badge: pendingGames.length > 0 ? pendingGames.length : undefined,
                },
              ] as Array<{ id: Tab; label: string; badge?: number }>
            ).map(tab => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-current={activeTab === tab.id ? 'true' : undefined}
                onClick={() => setActiveTab(tab.id)}
                className={`px-1 py-2 -mb-px border-b-2 font-medium transition-colors flex items-center gap-1 ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.badge !== undefined && (
                  <span className="ml-1 text-xs bg-primary-100 text-primary-700 rounded-full px-1.5 py-0.5">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* Filters */}
          <div className="mt-4 flex flex-wrap gap-3 items-center">
            {isAuthenticated && activeTab !== 'proposals' && (
              <ScopeToggle
                scope={scope}
                onChange={setScope}
                mineLabel="Mine"
                allLabel="All"
                ariaLabel="Show my games or all games"
              />
            )}
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="rounded-md border border-gray-300 text-sm shadow-sm px-3 py-2 bg-white focus:ring-primary-500 focus:border-primary-500"
              aria-label="Filter by category"
            >
              <option value="">All categories</option>
              {Object.values(Category).map(cat => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-4 rounded-md bg-danger-50 p-4" role="alert">
              <p className="text-sm text-danger-600">{error}</p>
              <button
                type="button"
                onClick={() =>
                  activeSeason?.id && dispatch(fetchGames({ seasonId: activeSeason.id }))
                }
                className="mt-2 text-sm text-primary-600 underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* ── Tab content ──────────────────────────────────────────────── */}

          {/* Scheduled tab */}
          {activeTab === 'scheduled' && (
            <section className="mt-6" role="tabpanel" aria-label="Scheduled games">
              {loading && (
                <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
                  {[1, 2, 3].map(i => (
                    <SkeletonRow key={i} />
                  ))}
                </div>
              )}

              {!loading && scheduledGames.length === 0 && (
                <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                  {showOnlyMine ? (
                    <>
                      <p className="text-gray-500">You have no scheduled games.</p>
                      <button
                        type="button"
                        onClick={() => setScope('all')}
                        className="mt-3 text-sm text-primary-600 hover:underline"
                      >
                        Show all games
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-500">No scheduled games yet.</p>
                      <button
                        type="button"
                        onClick={handlePropose}
                        className="mt-3 text-sm text-primary-600 hover:underline"
                      >
                        Propose a game
                      </button>
                    </>
                  )}
                </div>
              )}

              {!loading && scheduledGames.length > 0 && (
                <div className="space-y-6">
                  {Array.from(scheduledGroups.entries()).map(([dateKey, dayGames]) => (
                    <div key={dateKey}>
                      <h3 className="text-sm font-semibold text-gray-500 mb-2">{dateKey}</h3>
                      <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
                        {dayGames.map(game => (
                          <GameSummaryRow
                            key={game.id}
                            {...toGameRowProps(gameToRowInput(game, bannerCtx))}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Results tab */}
          {activeTab === 'results' && (
            <section className="mt-6" role="tabpanel" aria-label="Results">
              {loading && (
                <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
                  {[1, 2, 3].map(i => (
                    <SkeletonRow key={i} />
                  ))}
                </div>
              )}

              {!loading && completedGames.length === 0 && (
                <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                  {showOnlyMine ? (
                    <>
                      <p className="text-gray-500">You have no completed games.</p>
                      <button
                        type="button"
                        onClick={() => setScope('all')}
                        className="mt-3 text-sm text-primary-600 hover:underline"
                      >
                        Show all results
                      </button>
                    </>
                  ) : (
                    <p className="text-gray-500">No completed games yet.</p>
                  )}
                </div>
              )}

              {!loading && completedGames.length > 0 && (
                <div className="space-y-6">
                  {Array.from(completedGroups.entries()).map(([dateKey, dayGames]) => (
                    <div key={dateKey}>
                      <h3 className="text-sm font-semibold text-gray-500 mb-2">{dateKey}</h3>
                      <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
                        {dayGames.map(game => (
                          <GameSummaryRow
                            key={game.id}
                            {...toGameRowProps(gameToRowInput(game, bannerCtx))}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Proposals tab */}
          {activeTab === 'proposals' && (
            <section className="mt-6" role="tabpanel" aria-label="Proposals">
              {!isAuthenticated && (
                <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                  <p className="text-gray-500">
                    <Link to="/login" className="text-primary-600 hover:underline">
                      Log in
                    </Link>{' '}
                    to view your game proposals.
                  </p>
                </div>
              )}

              {isAuthenticated && loading && (
                <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
                  {[1, 2].map(i => (
                    <SkeletonRow key={i} />
                  ))}
                </div>
              )}

              {isAuthenticated && !loading && visibleProposals.length === 0 && (
                <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                  <p className="text-gray-500">
                    {categoryFilter && proposals.length > 0
                      ? 'No proposals in this category.'
                      : 'No proposals yet.'}
                  </p>
                  {!(categoryFilter && proposals.length > 0) && (
                    <button
                      type="button"
                      onClick={handlePropose}
                      className="mt-3 text-sm text-primary-600 hover:underline"
                    >
                      Propose a game
                    </button>
                  )}
                </div>
              )}

              {isAuthenticated && !loading && visibleProposals.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
                  {visibleProposals.map((proposal: GameProposal) => (
                    <GameSummaryRow
                      key={proposal.id}
                      {...toGameRowProps(proposalToRowInput(proposal, bannerCtx))}
                      onApproveProposal={handleApproveProposal}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Pending tab — pre-built fixtures (to propose, or under proposal) */}
          {activeTab === 'pending' && (
            <section className="mt-6" role="tabpanel" aria-label="Pending games">
              {loading && (
                <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
                  {[1, 2, 3].map(i => (
                    <SkeletonRow key={i} />
                  ))}
                </div>
              )}

              {!loading && pendingGames.length === 0 && (
                <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                  {showOnlyMine ? (
                    <>
                      <p className="text-gray-500">You have no games pending to propose.</p>
                      <button
                        type="button"
                        onClick={() => setScope('all')}
                        className="mt-3 text-sm text-primary-600 hover:underline"
                      >
                        Show all games
                      </button>
                    </>
                  ) : (
                    <p className="text-gray-500">No pending games.</p>
                  )}
                </div>
              )}

              {!loading && pendingGames.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
                  {pendingGames.map(game => (
                    <GameSummaryRow
                      key={game.id}
                      {...toGameRowProps(gameToRowInput(game, bannerCtx))}
                      onApproveProposal={handleApproveProposal}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {/* Public browse notice */}
      <p className="mt-6 text-xs text-gray-400">
        Viewing is public; proposing or responding to a game requires logging in.
      </p>
    </div>
  );
};

export default GamesPage;
